import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';

/**
 * Collects the reason §23.8 requires before a volunteer is refused.
 *
 * ⚠️ Replaces `window.prompt`, which worked and was still wrong for this particular decision.
 *
 * A native prompt gives a one-line box with no label, no explanation of who will read the text, and no
 * validation — and the reason here is passed on to the person being refused. It also cannot be styled,
 * so it appears in the browser's own language and direction regardless of the panel's, which on an
 * Arabic screen means an English dialog with a left-aligned field.
 */
export function RejectVolunteerDialog({
  volunteerName,
  open,
  onOpenChange,
  onConfirm,
}: {
  volunteerName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useLocale();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    // Trimmed before the check: whitespace is not a reason, and the server's validator rejects it
    // anyway — refusing here means the operator finds out before the request rather than after.
    const trimmed = reason.trim();

    if (!trimmed) {
      setError(t.volunteers.rejectReasonRequired);
      return;
    }

    onConfirm(trimmed);
    setReason('');
    setError(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Cleared on close so a reason typed for one volunteer cannot be submitted for the next —
        // which is exactly the mistake a reused dialog invites.
        if (!next) {
          setReason('');
          setError(null);
        }

        onOpenChange(next);
      }}
    >
      <DialogContent closeLabel={t.volunteers.close}>
        <DialogHeader>
          <DialogTitle>{t.volunteers.rejectDialogTitle}</DialogTitle>
          <DialogDescription>{t.volunteers.rejectDialogLede}</DialogDescription>
        </DialogHeader>

        {/* The name is shown inside the dialog, not only in the row behind it: an overlay covers the
            table, and "refuse this volunteer" with no name is how the wrong person gets refused. */}
        <p className="font-semibold">{volunteerName}</p>

        <Field label={t.volunteers.rejectReasonLabel} htmlFor="reject-reason">
          <textarea
            id="reject-reason"
            rows={3}
            autoFocus
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setError(null);
            }}
            placeholder={t.volunteers.rejectReasonPlaceholder}
            className="w-full rounded-md border border-border bg-surface-elevated px-3 py-2 text-start outline-none focus:border-primary"
          />
        </Field>

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t.volunteers.cancel}
          </Button>
          <Button variant="danger" onClick={submit}>
            {t.volunteers.confirmReject}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
